'use client';

import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { Icon, type IconName } from '../../icons';
import type {
  ActiveFilter,
  FilterDefinition,
  FilterOperator,
  FilterFieldType,
} from '@/types/tableViews';
import { OPERATORS_FOR_TYPE, OPERATOR_LABELS } from '@/types/tableViews';

import './FilterChip.css';

export interface FilterChipProps {
  filter: ActiveFilter;
  definition: FilterDefinition;
  allDefinitions: FilterDefinition[];
  onUpdate: (filterId: string, updates: Partial<Pick<ActiveFilter, 'operator' | 'value' | 'filterKey'>>) => void;
  onRemove: (filterId: string) => void;
  className?: string;
}

type PopoverType = 'field' | 'operator' | 'value' | null;

export function FilterChip({
  filter,
  definition,
  allDefinitions,
  onUpdate,
  onRemove,
  className = '',
}: FilterChipProps) {
  const [openPopover, setOpenPopover] = useState<PopoverType>(null);
  const chipRef = useRef<HTMLDivElement | null>(null);
  const fieldRef = useRef<HTMLButtonElement | null>(null);
  const operatorRef = useRef<HTMLButtonElement | null>(null);
  const valueRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  const anchorForPopover = (type: PopoverType): React.RefObject<HTMLElement | null> => {
    switch (type) {
      case 'field': return fieldRef;
      case 'operator': return operatorRef;
      case 'value': return valueRef;
      default: return chipRef;
    }
  };

  useEffect(() => {
    if (!openPopover) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (chipRef.current?.contains(t)) return;
      if (popoverRef.current?.contains(t)) return;
      setOpenPopover(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenPopover(null);
    };
    window.addEventListener('mousedown', onDown, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [openPopover]);

  const operators = OPERATORS_FOR_TYPE[definition.type] ?? ['is', 'is_not'];

  const displayValue = (() => {
    if (Array.isArray(filter.value)) {
      return filter.value
        .map((v) => definition.options?.find((o) => o.value === v)?.label ?? v)
        .join(', ');
    }
    if (definition.type === 'date') {
      return formatDateLabel(filter.value as string);
    }
    return definition.options?.find((o) => o.value === filter.value)?.label ?? filter.value;
  })();

  return (
    <div ref={chipRef} className={`filter-chip-v2 ${className}`.trim()}>
      <button
        ref={fieldRef}
        className="filter-chip-v2__segment filter-chip-v2__field"
        onClick={() => setOpenPopover(openPopover === 'field' ? null : 'field')}
        type="button"
      >
        <Icon name={(definition.icon as IconName) || 'Filter'} size={13} />
        <span>{definition.label}</span>
      </button>

      <button
        ref={operatorRef}
        className="filter-chip-v2__segment filter-chip-v2__operator"
        onClick={() => setOpenPopover(openPopover === 'operator' ? null : 'operator')}
        type="button"
      >
        {OPERATOR_LABELS[filter.operator]}
      </button>

      <button
        ref={valueRef}
        className="filter-chip-v2__segment filter-chip-v2__value"
        onClick={() => setOpenPopover(openPopover === 'value' ? null : 'value')}
        type="button"
      >
        {displayValue || '...'}
      </button>

      <button
        className="filter-chip-v2__segment filter-chip-v2__remove"
        onClick={() => onRemove(filter.id)}
        type="button"
        aria-label={`Remove ${definition.label} filter`}
      >
        <Icon name="X" size={12} />
      </button>

      <AnimatePresence>
        {openPopover === 'field' && (
          <ChipPopover ref={popoverRef} anchorRef={anchorForPopover('field')}>
            <div className="filter-chip-v2__popover-list">
              {allDefinitions.map((def) => (
                <button
                  key={def.key}
                  className={`filter-chip-v2__popover-item ${def.key === filter.filterKey ? 'filter-chip-v2__popover-item--selected' : ''}`}
                  onClick={() => {
                    onUpdate(filter.id, { filterKey: def.key });
                    setOpenPopover(null);
                  }}
                  type="button"
                >
                  {def.label}
                </button>
              ))}
            </div>
          </ChipPopover>
        )}
        {openPopover === 'operator' && (
          <ChipPopover ref={popoverRef} anchorRef={anchorForPopover('operator')}>
            <div className="filter-chip-v2__popover-list">
              {operators.map((op) => (
                <button
                  key={op}
                  className={`filter-chip-v2__popover-item ${op === filter.operator ? 'filter-chip-v2__popover-item--selected' : ''}`}
                  onClick={() => {
                    onUpdate(filter.id, { operator: op });
                    setOpenPopover(null);
                  }}
                  type="button"
                >
                  {OPERATOR_LABELS[op]}
                </button>
              ))}
            </div>
          </ChipPopover>
        )}
        {openPopover === 'value' && (
          <ChipPopover ref={popoverRef} anchorRef={anchorForPopover('value')}>
            <ValueEditor
              filter={filter}
              definition={definition}
              onUpdate={onUpdate}
              onClose={() => setOpenPopover(null)}
            />
          </ChipPopover>
        )}
      </AnimatePresence>
    </div>
  );
}

interface ChipPopoverProps {
  anchorRef: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
}

const ChipPopover = React.forwardRef<HTMLDivElement, ChipPopoverProps>(
  function ChipPopover({ anchorRef, children }, ref) {
    const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

    useLayoutEffect(() => {
      if (!anchorRef.current) return;
      const rect = anchorRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left });
    }, [anchorRef]);

    return createPortal(
      <motion.div
        ref={ref}
        className="filter-chip-v2__popover"
        style={pos ? { position: 'fixed', top: pos.top, left: pos.left } : undefined}
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -2 }}
        transition={{ duration: 0.12 }}
      >
        {children}
      </motion.div>,
      document.body,
    );
  },
);

interface ValueEditorProps {
  filter: ActiveFilter;
  definition: FilterDefinition;
  onUpdate: (filterId: string, updates: Partial<Pick<ActiveFilter, 'value' | 'operator'>>) => void;
  onClose: () => void;
}

function ValueEditor({ filter, definition, onUpdate, onClose }: ValueEditorProps) {
  const [search, setSearch] = useState('');
  const [textVal, setTextVal] = useState(
    typeof filter.value === 'string' ? filter.value : '',
  );

  if (definition.type === 'select' || definition.type === 'multi_select') {
    const currentValues = Array.isArray(filter.value) ? filter.value : [filter.value];
    const options = (definition.options ?? []).filter((option) => option.label.toLowerCase().includes(search.toLowerCase()));
    return (
      <div className="filter-chip-v2__popover-list">
        <div className="filter-chip-v2__popover-header">{definition.label}</div>
        {definition.searchable && (
          <div className="filter-chip-v2__popover-text">
            <input
              className="filter-chip-v2__text-input"
              aria-label={`Search ${definition.label.toLowerCase()}`}
              placeholder={`Search ${definition.label.toLowerCase()}...`}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              autoFocus
            />
          </div>
        )}
        {options.map((opt) => {
          const isSelected = currentValues.includes(opt.value);
          return (
            <button
              key={opt.value}
              className={`filter-chip-v2__popover-item ${isSelected ? 'filter-chip-v2__popover-item--selected' : ''}`}
              data-activity-label={`${definition.label} filter option`}
              onClick={() => {
                if (definition.type === 'multi_select') {
                  const next = isSelected
                    ? currentValues.filter((v) => v !== opt.value)
                    : [...currentValues, opt.value];
                  onUpdate(filter.id, { value: next });
                } else {
                  onUpdate(filter.id, {
                    value: opt.value,
                    ...(filter.operator === 'contains' ? { operator: 'is' as const } : filter.operator === 'does_not_contain' ? { operator: 'is_not' as const } : {}),
                  });
                  onClose();
                }
              }}
              type="button"
            >
              {definition.type === 'multi_select' && (
                <span className={`filter-chip-v2__check ${isSelected ? 'filter-chip-v2__check--active' : ''}`}>
                  {isSelected && <Icon name="Check" size={12} />}
                </span>
              )}
              <span>{opt.label}</span>
            </button>
          );
        })}
        {options.length === 0 && <div className="filter-chip-v2__popover-header" role="status">{definition.optionsMessage ?? 'No results'}</div>}
      </div>
    );
  }

  if (definition.type === 'date') {
    return (
      <div className="filter-chip-v2__popover-dates">
        <input
          type="date"
          className="filter-chip-v2__date-input"
          value={textVal}
          onChange={(e) => {
            setTextVal(e.target.value);
            if (e.target.value) {
              onUpdate(filter.id, { value: e.target.value });
            }
          }}
        />
      </div>
    );
  }

  if (definition.type === 'boolean') {
    return (
      <div className="filter-chip-v2__popover-list">
        {[
          { value: 'true', label: 'Yes' },
          { value: 'false', label: 'No' },
        ].map((opt) => (
          <button
            key={opt.value}
            className={`filter-chip-v2__popover-item ${filter.value === opt.value ? 'filter-chip-v2__popover-item--selected' : ''}`}
            onClick={() => {
              onUpdate(filter.id, { value: opt.value });
              onClose();
            }}
            type="button"
          >
            {opt.label}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="filter-chip-v2__popover-text">
      <input
        type="text"
        className="filter-chip-v2__text-input"
        value={textVal}
        onChange={(e) => setTextVal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            onUpdate(filter.id, { value: textVal });
            onClose();
          }
        }}
        placeholder="Type a value..."
        autoFocus
      />
    </div>
  );
}

function formatDateLabel(value: string): string {
  if (!value) return '...';
  try {
    const d = new Date(value + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return value;
  }
}
